import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { castMembers } from "@/mocks/cast";

export default function CastPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useSeo({
    title: "演员表查询 — 按演员姓名反查参演短剧",
    description:
      "输入演员姓名即可查询其参演的全部爆料短剧，查看角色名称、代表作品与演技风格简介，快速完成短剧演员表查询。",
    keywords: "演员表查询,爆料短剧,短剧演员,演员资料,导演编剧信息",
    path: "/cast",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "演员表查询",
      url: "https://beesfly.cn/cast",
      description: "按演员姓名查询参演短剧与饰演角色的演员资料库。",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: castMembers.map((member, index) => ({
          "@type": "ListItem",
          position: index + 1,
          item: { "@type": "Person", name: member.name, description: member.bio },
        })),
      },
    },
  });

  const filtered = useMemo(() => {
    if (!query) return castMembers;
    return castMembers.filter((member) =>
      `${member.name}${member.role}${member.bestKnown}${member.works.join("")}`.includes(query),
    );
  }, [query]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    const next = new URLSearchParams();
    if (value) next.set("q", value);
    setSearchParams(next, { replace: true });
  };

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "演员表查询" }]} />
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
            演员表查询
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            收录 {castMembers.length} 位常驻短剧演员。输入姓名即可反查参演作品与饰演角色，
            也可以顺着代表作直接跳转到对应短剧详情页查看完整演员表。
          </p>

          <form
            onSubmit={submit}
            role="search"
            className="mt-7 flex w-full max-w-xl flex-col gap-2 rounded-lg border border-background-300 bg-background-50 p-2 sm:flex-row sm:items-center sm:rounded-full"
          >
            <span className="hidden h-7 w-7 items-center justify-center pl-2 text-foreground-500 sm:flex">
              <i className="ri-user-search-line text-lg"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="输入演员姓名，例如：沈砚青"
              aria-label="搜索演员"
              className="w-full flex-1 bg-transparent px-4 py-2 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 sm:px-1"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 sm:rounded-full"
            >
              查询演员
            </button>
          </form>
        </div>
      </section>

      <section className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
        {query && (
          <p className="text-sm text-foreground-600">
            关于「<span className="text-foreground-950">{query}</span>」共找到 {filtered.length} 位演员
          </p>
        )}

        {filtered.length > 0 ? (
          <div className="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((member) => (
              <article
                key={member.id}
                className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
              >
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md bg-background-200">
                    <img
                      src={member.avatar}
                      alt={`演员${member.name}资料照片`}
                      title={`${member.name} 演员表查询`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0">
                    <h2 className="font-heading text-base font-bold text-foreground-950">
                      {member.name}
                    </h2>
                    <p className="mt-1 text-xs text-foreground-500">{member.role}</p>
                    <p className="mt-1 text-xs text-accent-700">参演 {member.dramaCount} 部</p>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-relaxed text-foreground-600">{member.bio}</p>

                <div className="mt-4">
                  <p className="text-xs text-foreground-500">代表作</p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {member.works.map((work) => (
                      <Link
                        key={work}
                        to={`/dramas?q=${encodeURIComponent(work)}`}
                        className="rounded-full bg-background-100 px-2.5 py-0.5 text-[11px] text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                      >
                        {work}
                      </Link>
                    ))}
                  </div>
                </div>

                <Link
                  to={`/dramas?q=${encodeURIComponent(member.name)}`}
                  className="mt-5 inline-flex w-fit items-center gap-1.5 whitespace-nowrap text-sm text-primary-600 hover:text-primary-700"
                >
                  查看参演短剧
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-user-unfollow-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-950">
              暂未收录这位演员
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              演员库仍在持续补充，你可以换个姓名试试，或直接去短剧库按剧名查找。
            </p>
            <Link
              to="/dramas"
              className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              去短剧库查找
            </Link>
          </div>
        )}
      </section>
    </SiteLayout>
  );
}